import { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

export const TREATMENTS_NAV = [
  { name: 'Angst', slug: 'angst', category: 'Følelsesmæssig balance' },
  { name: 'Rygestop', slug: 'rygestop', category: 'Vaner & livsstil' },
  { name: 'Vægttab', slug: 'vaegttab', category: 'Vaner & livsstil' },
  { name: 'Jalousi', slug: 'jalousi', category: 'Følelsesmæssig balance' },
  { name: 'Barnløshed', slug: 'barnloeshed', category: 'Krop & sind' },
  { name: 'Søvnløshed', slug: 'soevnloeshed', category: 'Krop & sind' },
  { name: 'Depression', slug: 'depression', category: 'Følelsesmæssig balance' },
  { name: 'Sengevædning', slug: 'sengevaedning', category: 'Børn & unge' },
  { name: 'Hjemve', slug: 'hjemve', category: 'Børn & unge' },
  { name: 'Lavt selvværd', slug: 'lavt-selvvaerd', category: 'Følelsesmæssig balance' },
  { name: 'ADHD', slug: 'adhd', category: 'Børn & unge' },
  { name: 'Skoleværing', slug: 'skolevaering', category: 'Børn & unge' },
  { name: 'Særlig sensitiv', slug: 'saerlig-sensitiv', category: 'Følelsesmæssig balance' },
  { name: 'Eksamensangst', slug: 'eksamens-angst', category: 'Følelsesmæssig balance' },
  { name: 'Stress', slug: 'stress', category: 'Følelsesmæssig balance' },
  { name: 'Flyskræk', slug: 'flyskraek', category: 'Følelsesmæssig balance' },
  { name: 'Ring og spørg', slug: 'ring-og-spoerg', category: 'Øvrige områder' },
]

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [treatmentsOpen, setTreatmentsOpen] = useState(false)
  const [mobileTreatmentsOpen, setMobileTreatmentsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const treatmentsDropdownRef = useRef(null)

  // Track scroll position to shrink header & logo when sticky/fixed
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false)
    setTreatmentsOpen(false)
    setMobileTreatmentsOpen(false)
  }, [location.pathname])

  // Close dropdown on click outside or Escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        treatmentsDropdownRef.current &&
        !treatmentsDropdownRef.current.contains(event.target)
      ) {
        setTreatmentsOpen(false)
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setTreatmentsOpen(false)
        setMobileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  // Automatically close mobile menu if resized to 1200px or above
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1200) {
        setMobileOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isTreatmentsActive =
    location.pathname.startsWith('/behandlinger') ||
    location.pathname === '/hypnose' ||
    location.pathname === '/hypnoterapi' ||
    TREATMENTS_NAV.some((t) => location.pathname === `/${t.slug}`)

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b transition-colors duration-200 ${
        scrolled
          ? 'border-[#DFE5D7] bg-[#F7F9F4]/98 shadow-sm shadow-[#242E1F]/5 backdrop-blur-md'
          : 'border-[#E4EADF]/80 bg-[#F7F9F4]/95 backdrop-blur-md'
      }`}
    >
      {/* Main navigation container */}
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 sm:px-6 py-3 sm:py-3.5">
        {/* Brand / Logo */}
        <Link
          to="/"
          className="group flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-[#556B48] rounded"
        >
          <span className="font-serif font-medium tracking-tight text-[#2B3824] text-lg sm:text-[23px] group-hover:text-[#5C7A4F] transition-colors">
            Trine Rosendal
          </span>
          <span className="font-normal tracking-wider text-[#697A62] uppercase text-[9px] sm:text-[10.5px] -mt-0.5">
            Hypnoterapi & Healing · Holstebro
          </span>
        </Link>

        {/* Minimalist Desktop Navigation (shown on screens 1200px and wider) */}
        <nav
          aria-label="Hovednavigation"
          className="hidden min-[1200px]:flex items-center gap-1.5 lg:gap-2 text-[14px] lg:text-[15px]"
        >
          {/* Behandlinger (with clear, calm dropdown) */}
          <div ref={treatmentsDropdownRef} className="relative">
            <div className="inline-flex items-center rounded-full transition">
              <NavLink
                to="/behandlinger"
                className={({ isActive }) =>
                  `pl-3.5 pr-1.5 py-2 font-medium transition ${
                    isTreatmentsActive
                      ? 'text-[#3D2536] font-semibold'
                      : 'text-[#44543E] hover:text-[#242E1F]'
                  }`
                }
              >
                Behandlinger
              </NavLink>
              <button
                type="button"
                onClick={() => setTreatmentsOpen((prev) => !prev)}
                className={`pr-3 py-2 cursor-pointer transition text-[#7A6474] hover:text-[#3D2536] ${
                  isTreatmentsActive ? 'text-[#3D2536]' : ''
                }`}
                aria-expanded={treatmentsOpen}
                aria-label="Åbn menu for behandlinger"
              >
                <i
                  className={`fa-solid fa-chevron-down text-[11px] transition-transform duration-200 ${
                    treatmentsOpen ? 'rotate-180 text-[#3F5236]' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
            </div>

            {/* Clean, minimalist Dropdown */}
            {treatmentsOpen && (
              <div className="absolute left-0 top-full pt-2 w-80 lg:w-[380px] z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                <div className="rounded-2xl border border-[#DFE6D5] bg-[#F6F8F3] p-4 sm:p-5 shadow-xl shadow-[#4A3243]/8 space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-[#EDE2EB]">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#7A6474]">
                      Behandling
                    </span>
                    <Link
                      to="/behandlinger"
                      onClick={() => setTreatmentsOpen(false)}
                      className={`text-xs font-medium underline underline-offset-2 transition ${
                        location.pathname === '/behandlinger'
                          ? 'text-[#5C7A4F] font-semibold bg-[#E7EEDF] px-2 py-0.5 rounded-md no-underline'
                          : 'text-[#5C7A4F] hover:text-[#321F2E]'
                      }`}
                    >
                      Se alle 17 områder →
                    </Link>
                  </div>

                  <div className="pt-1 space-y-1.5">
                    <Link
                      to="/hypnose"
                      onClick={() => setTreatmentsOpen(false)}
                      className={`flex items-start gap-3 rounded-xl p-3 transition duration-150 ${
                        location.pathname === '/hypnose' || location.pathname === '/hypnoterapi'
                          ? 'bg-[#5C7A4F] text-white shadow-sm ring-1 ring-[#5E4258]'
                          : 'hover:bg-[#F3EBF1] text-[#382333]'
                      }`}
                    >
                      <div
                        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition ${
                          location.pathname === '/hypnose' || location.pathname === '/hypnoterapi'
                            ? 'bg-white/20 text-white'
                            : 'bg-[#E7EEDF] text-[#5C7A4F]'
                        }`}
                      >
                        <i className="fa-solid fa-brain text-sm" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-sm font-medium ${
                            location.pathname === '/hypnose' || location.pathname === '/hypnoterapi'
                              ? 'text-white font-semibold'
                              : 'text-[#382333]'
                          }`}
                        >
                          Hypnoterapi
                        </p>
                        <p
                          className={`text-xs mt-0.5 ${
                            location.pathname === '/hypnose' || location.pathname === '/hypnoterapi'
                              ? 'text-[#F3EBF1]'
                              : 'text-[#705C6B]'
                          }`}
                        >
                          Forløs ubevidste blokeringer, angst, vaner og genskab indre ro
                        </p>
                      </div>
                      {(location.pathname === '/hypnose' || location.pathname === '/hypnoterapi') && (
                        <span
                          className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/25 text-[10px] text-white"
                          aria-hidden="true"
                        >
                          <i className="fa-solid fa-check" />
                        </span>
                      )}
                    </Link>

                    <Link
                      to="/behandlinger"
                      onClick={() => setTreatmentsOpen(false)}
                      className="flex items-center justify-between rounded-xl p-2.5 text-xs font-medium text-[#5C7A4F] hover:bg-[#F3EBF1] transition"
                    >
                      <span className="flex items-center gap-2">
                        <i className="fa-solid fa-list-check text-[11px]" aria-hidden="true" />
                        <span>Behandlingsoversigt (17 problemstillinger)</span>
                      </span>
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Om Trine */}
          <NavLink
            to="/profil"
            className={({ isActive }) =>
              `px-3 py-2 rounded-full font-medium transition ${
                isActive
                  ? 'text-[#3D2536] font-semibold'
                  : 'text-[#44543E] hover:text-[#242E1F]'
              }`
            }
          >
            Om Trine
          </NavLink>

          {/* Priser */}
          <NavLink
            to="/priser"
            className={({ isActive }) =>
              `px-3 py-2 rounded-full font-medium transition ${
                isActive
                  ? 'text-[#3D2536] font-semibold'
                  : 'text-[#44543E] hover:text-[#242E1F]'
              }`
            }
          >
            Priser
          </NavLink>

          {/* Referencer */}
          <NavLink
            to="/referencer"
            className={({ isActive }) =>
              `px-3 py-2 rounded-full font-medium transition ${
                isActive
                  ? 'text-[#3D2536] font-semibold'
                  : 'text-[#44543E] hover:text-[#242E1F]'
              }`
            }
          >
            Referencer
          </NavLink>

          {/* Kontakt */}
          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `px-3 py-2 rounded-full font-medium transition ${
                isActive
                  ? 'text-[#3D2536] font-semibold'
                  : 'text-[#44543E] hover:text-[#242E1F]'
              }`
            }
          >
            Kontakt
          </NavLink>

          {/* Facebook Link & Book tid CTA Button */}
          <div className="pl-2 flex items-center gap-2">
            <a
              href="https://www.facebook.com/hypnosetrine/#"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#5C7A4F] hover:bg-[#E7EEDF] transition"
              title="Følg Trine Rosendal på Facebook"
              aria-label="Følg Trine Rosendal på Facebook"
            >
              <i className="fa-brands fa-facebook text-lg" aria-hidden="true" />
            </a>

            <a
              href="https://system.easypractice.net/book/online-booking-16"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#F5D154] hover:bg-[#EABF35] px-5 py-2.5 text-sm font-semibold text-[#1E2718] shadow-xs transition duration-200 active:scale-[0.98]"
            >
              <span>Book tid</span>
              <i className="fa-solid fa-arrow-right text-xs text-[#1E2718]/70" aria-hidden="true" />
            </a>
          </div>
        </nav>

        {/* Mobile / Tablet Header Actions (shown until 1200px) */}
        <div className="flex min-[1200px]:hidden items-center gap-2.5">
          <a
            href="https://system.easypractice.net/book/online-booking-16"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#F5D154] hover:bg-[#EABF35] px-3.5 py-1.5 text-xs font-semibold text-[#1E2718] shadow-xs transition"
          >
            <span>Book tid</span>
          </a>

          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#DFE6D5] bg-[#F6F8F3] text-[#4A3243] hover:bg-[#F4EBF1] focus:outline-none"
            aria-label={mobileOpen ? 'Luk menu' : 'Åbn menu'}
            aria-expanded={mobileOpen}
          >
            <i
              className={`fa-solid text-base ${mobileOpen ? 'fa-xmark' : 'fa-bars'}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Simple, Minimalist Mobile Drawer (until 1200px) */}
      {mobileOpen && (
        <div className="min-[1200px]:hidden fixed inset-0 top-[57px] sm:top-[65px] z-50 bg-[#2B3824]/25 backdrop-blur-xs">
          <div className="h-[calc(100vh-57px)] sm:h-[calc(100vh-65px)] w-full max-w-sm ml-auto bg-[#F6F8F3] border-l border-[#DFE6D5] shadow-2xl flex flex-col justify-between overflow-y-auto p-6 animate-in slide-in-from-right duration-200">
            <div className="space-y-4">
              {/* Main Links */}
              <nav className="space-y-1 text-base">
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) =>
                    `block px-4 py-2.5 rounded-xl font-medium transition ${
                      isActive
                        ? 'bg-[#E7EEDF] text-[#222E1D]'
                        : 'text-[#3E4E36] hover:bg-[#EEF4E9]'
                    }`
                  }
                >
                  Forside
                </NavLink>

                {/* Behandlinger Simple Accordion */}
                <div className="rounded-xl border border-[#DFE6D5] bg-[#FAFBF7] overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <NavLink
                      to="/behandlinger"
                      className="font-medium text-[#24301D]"
                    >
                      Behandlinger
                    </NavLink>
                    <button
                      type="button"
                      onClick={() => setMobileTreatmentsOpen(!mobileTreatmentsOpen)}
                      className="p-1 text-[#667A5C]"
                      aria-label="Fold behandlinger ud"
                    >
                      <i
                        className={`fa-solid fa-chevron-down text-xs transition-transform ${
                          mobileTreatmentsOpen ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  {mobileTreatmentsOpen && (
                    <div className="px-3 pb-3 pt-1 border-t border-[#DFE6D5] bg-[#F6F8F3] space-y-1.5 text-sm">
                      <Link
                        to="/hypnose"
                        className={`flex items-center justify-between px-3 py-2 rounded-lg transition ${
                          location.pathname === '/hypnose' || location.pathname === '/hypnoterapi'
                            ? 'bg-[#5C7A4F] text-white font-medium shadow-xs'
                            : 'text-[#3E4E36] hover:bg-[#EEF4E9]'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className="fa-solid fa-brain text-xs text-[#556B48]" aria-hidden="true" />
                          <span>Hypnoterapi</span>
                        </span>
                        {(location.pathname === '/hypnose' || location.pathname === '/hypnoterapi') && (
                          <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                        )}
                      </Link>

                      <Link
                        to="/behandlinger"
                        className={`flex items-center justify-between px-3 py-2 rounded-lg font-medium transition ${
                          location.pathname === '/behandlinger'
                            ? 'bg-[#5C7A4F] text-white font-semibold shadow-xs'
                            : 'text-[#5C7A4F] hover:bg-[#EEF4E9]'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className="fa-solid fa-list-check text-xs" aria-hidden="true" />
                          <span>Se alle 17 behandlingsområder</span>
                        </span>
                        {location.pathname === '/behandlinger' && (
                          <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                        )}
                      </Link>
                    </div>
                  )}
                </div>

                <NavLink
                  to="/profil"
                  className={({ isActive }) =>
                    `block px-4 py-2.5 rounded-xl font-medium transition ${
                      isActive
                        ? 'bg-[#E7EEDF] text-[#222E1D]'
                        : 'text-[#3E4E36] hover:bg-[#EEF4E9]'
                    }`
                  }
                >
                  Om Trine
                </NavLink>

                <NavLink
                  to="/priser"
                  className={({ isActive }) =>
                    `block px-4 py-2.5 rounded-xl font-medium transition ${
                      isActive
                        ? 'bg-[#E7EEDF] text-[#222E1D]'
                        : 'text-[#3E4E36] hover:bg-[#EEF4E9]'
                    }`
                  }
                >
                  Priser
                </NavLink>

                <NavLink
                  to="/referencer"
                  className={({ isActive }) =>
                    `block px-4 py-2.5 rounded-xl font-medium transition ${
                      isActive
                        ? 'bg-[#E7EEDF] text-[#222E1D]'
                        : 'text-[#3E4E36] hover:bg-[#EEF4E9]'
                    }`
                  }
                >
                  Referencer
                </NavLink>

                <NavLink
                  to="/kontakt"
                  className={({ isActive }) =>
                    `block px-4 py-2.5 rounded-xl font-medium transition ${
                      isActive
                        ? 'bg-[#E7EEDF] text-[#222E1D]'
                        : 'text-[#3E4E36] hover:bg-[#EEF4E9]'
                    }`
                  }
                >
                  Kontakt
                </NavLink>
              </nav>

              {/* Primary Mobile Action */}
              <div className="pt-2 space-y-2">
                <a
                  href="https://system.easypractice.net/book/online-booking-16"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#F5D154] hover:bg-[#EABF35] px-4 py-3 text-sm font-semibold text-[#1E2718] shadow-xs"
                >
                  <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
                  <span>Book tid online</span>
                </a>

                <a
                  href="https://www.facebook.com/hypnosetrine/#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#DFE6D5] bg-[#F6F8F3] px-4 py-2.5 text-xs font-medium text-[#3F5236] hover:bg-[#E9F0E2] transition"
                >
                  <i className="fa-brands fa-facebook text-[#5C7A4F] text-sm" aria-hidden="true" />
                  <span>Følg Trine på Facebook</span>
                </a>
              </div>
            </div>

            {/* Quick Contact Info at Bottom of Mobile Menu */}
            <div className="border-t border-[#DFE6D5] pt-4 mt-6 text-xs text-[#705C6B] space-y-2">
              <a
                href="tel:42510105"
                className="flex items-center gap-2 font-medium text-[#40273A]"
              >
                <i className="fa-solid fa-phone text-[#556B48]" aria-hidden="true" />
                <span>42 51 01 05</span>
              </a>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-location-dot text-[#556B48]" aria-hidden="true" />
                <span>Prins Burisvej 41.A, 7500 Holstebro</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
